import { useCallback, useEffect, useState } from 'react'

import { type SilkActivityBannerResponse } from '@/api'
import { useOptionalHomeSiteContext } from '@/features/home/context/HomeSiteProvider'

const INIT: SilkActivityBannerResponse = {
  enabled: false,
  show_countdown: false,
  message: '',
  cta_label: '',
  cta_url: '',
  revision: 0,
}

const DISMISS_KEY = 'silkreels.activity_banner.dismissed'

export function useActivityBanner() {
  const ctx = useOptionalHomeSiteContext()
  const site = ctx?.site ?? null
  const loading = ctx?.loading ?? true
  const data = site?.activity_banner ?? INIT
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const rev = window.localStorage.getItem(DISMISS_KEY)
    setDismissed(rev === String(data.revision))
  }, [data.revision])

  const dismiss = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DISMISS_KEY, String(data.revision))
    }
    setDismissed(true)
  }, [data.revision])

  const visible = !loading && data.enabled && !dismissed && data.message.trim() !== ''

  return { banner: data, visible, dismiss }
}

export function useCountdown(endsAt?: string) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!endsAt) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [endsAt])

  if (!endsAt) return null
  const end = new Date(endsAt).getTime()
  const diff = Math.max(0, end - now)
  const days = Math.floor(diff / 86400000)
  const hours = Math.floor((diff % 86400000) / 3600000)
  const minutes = Math.floor((diff % 3600000) / 60000)
  const seconds = Math.floor((diff % 60000) / 1000)
  return { days, hours, minutes, seconds, expired: diff <= 0 }
}
